import { type FC, useState, useCallback } from 'react';
import type { NodeData, Priority } from '@/types/node.types';
import { NodeStatus, NODE_STATUS_LABELS } from '@/types/node.types';
import { useCanvasStore } from '@/stores/canvas.store';

interface NodeEditPanelProps {
  nodeId: string;
  data: NodeData;
  color: string;
  onClose: () => void;
}

const priorities: Priority[] = [1, 2, 3, 4, 5];
const statuses = Object.values(NodeStatus);

export const NodeEditPanel: FC<NodeEditPanelProps> = ({ nodeId, data, color, onClose }) => {
  const updateNode = useCanvasStore((s) => s.updateNode);
  const [title, setTitle] = useState(data.title);
  const [description, setDescription] = useState(data.description || '');
  const [priority, setPriority] = useState<Priority>(data.priority);
  const [status, setStatus] = useState<NodeStatus>(data.status);

  const handleSave = useCallback(async () => {
    await updateNode(nodeId, { title, description: description || undefined, priority, status });
    onClose();
  }, [nodeId, title, description, priority, status, updateNode, onClose]);

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      style={{
        position: 'absolute',
        top: '100%',
        left: 0,
        marginTop: 8,
        zIndex: 50,
        minWidth: 260,
        backgroundColor: 'var(--color-surface)',
        border: `1px solid ${color}`,
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-lg)',
        padding: 'var(--spacing-md)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--spacing-sm)',
      }}
    >
      {/* Title */}
      <label style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
        Titre
      </label>
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        style={{
          padding: '6px 8px',
          backgroundColor: 'var(--color-background)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-md)',
          color: 'var(--color-text)',
          fontSize: 'var(--font-size-sm)',
          outline: 'none',
        }}
      />

      {/* Description */}
      <label style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
        Description
      </label>
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        rows={2}
        style={{
          padding: '6px 8px',
          backgroundColor: 'var(--color-background)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-md)',
          color: 'var(--color-text)',
          fontSize: 'var(--font-size-sm)',
          outline: 'none',
          resize: 'vertical',
        }}
      />

      {/* Priority */}
      <label style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
        Priorité
      </label>
      <div style={{ display: 'flex', gap: 4 }}>
        {priorities.map((p) => (
          <button
            key={p}
            onClick={() => setPriority(p)}
            style={{
              flex: 1,
              padding: '4px 0',
              borderRadius: 'var(--radius-sm)',
              border: priority === p ? `2px solid ${color}` : '1px solid var(--color-border)',
              backgroundColor: priority === p ? `${color}30` : 'transparent',
              color: 'var(--color-text)',
              fontSize: 'var(--font-size-xs)',
              fontWeight: priority === p ? 700 : 400,
              cursor: 'pointer',
            }}
          >
            {p}
          </button>
        ))}
      </div>

      {/* Status */}
      <label style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
        Statut
      </label>
      <select
        value={status}
        onChange={(e) => setStatus(e.target.value as NodeStatus)}
        style={{
          padding: '6px 8px',
          backgroundColor: 'var(--color-background)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-md)',
          color: 'var(--color-text)',
          fontSize: 'var(--font-size-sm)',
          outline: 'none',
          cursor: 'pointer',
        }}
      >
        {statuses.map((s) => (
          <option key={s} value={s}>
            {NODE_STATUS_LABELS[s]}
          </option>
        ))}
      </select>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 'var(--spacing-sm)', marginTop: 'var(--spacing-xs)' }}>
        <button
          onClick={onClose}
          style={{
            flex: 1,
            padding: '6px',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-border)',
            backgroundColor: 'transparent',
            color: 'var(--color-text-muted)',
            fontSize: 'var(--font-size-sm)',
            cursor: 'pointer',
          }}
        >
          Annuler
        </button>
        <button
          onClick={handleSave}
          style={{
            flex: 1,
            padding: '6px',
            borderRadius: 'var(--radius-md)',
            border: 'none',
            backgroundColor: color,
            color: '#fff',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Enregistrer
        </button>
      </div>
    </div>
  );
};

export default NodeEditPanel;
